import React, { useCallback, useContext, useEffect, useState } from 'react';
import style from './style';
import { Grid, TextField } from '@mui/material';
import i18next from 'i18next';
import EAIcon from '../../../../components/common/EAIcon';
import moment from 'moment';
import ArchivedInfos from '../../ArchivedInfos';
import {
  PrivilegeEnum,
  RoleBasedAccessControlContext,
} from '../../../../services/common/RolesUtils';
import { Context } from '../../../../states/Store';
import EADialog from '../../../../components/common/EADialog';
import {
  updateContextInstance,
} from '../../../../services/edge/ContexteService';
import EADatePicker from '../../../../components/common/EADatePicker';
import { Controller, useForm } from 'react-hook-form';
import { getAdminSettingsBaseRoute } from '../../../../components/admin-settings/AdminSettingsUtils';

export const getExpirationDate = (date) => {
  const m = moment(date);
  return m.isValid()
    ? m.format('DD/MM/YYYY')
    : i18next.t('context.dataTable.noExpirationDate');
};

const Informations = ({ context, fetchContextInstance }) => {
  const { classes } = style();
  const [state] = useContext(Context);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const { isGlobalAdmin, hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);
  const formMethods = useForm({
    mode: 'onChange',
    delayError: 500,
    defaultValues: {
      context,
    },
  });

  useEffect(() => {
    if (!context) return;
    formMethods.reset({ context });
  }, [context, formMethods]);

  const { control, getValues, reset } = formMethods;

  useEffect(() => {
    reset({ context });
    if (!isEditingDescription) {
      setEditedDescription(context?.description ?? '');
    }
  }, [context, reset, isEditingDescription]);

  const shouldDisplayInfo = hasAnyOfPrivileges(
    PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_INFORMATION,
  ) && !context.isArchived;

  const canEdit = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT);

  const expDateInfos = () => {
    return (
      <>
        <div className={classes.label}>
          {i18next.t('context.contextDetail.expirationDate')}
        </div>
        <div className={classes.expirationDate}>
          <Controller
            control={control}
            name={`context.expirationDate`}
            render={({ field }) => (
              <EADatePicker
                {...field}
                minDate={moment().add(1, 'days')}
                formMethods={formMethods}
                name={`context.expirationDate`}
                onlyDate
                ref={null}
                onChangeCustom={(expDate) => saveDescriptionOrDate(expDate)}
                isDisabled={!canEdit}
              />
            )}
          />
        </div>
      </>
    );
  };

  const openModelPage = () => {
    window.open(
      `/${state.account.id}/${getAdminSettingsBaseRoute()}?tab=context&model=${context.contextModelId}`,
      '_blank',
    );
  };

  const saveDescriptionOrDate = useCallback(async () => {
    const expirationDate = getValues().context.expirationDate;
    const parsedDate = expirationDate
      ? moment(expirationDate, 'YYYY-MM-DD[T]HH:mm').format(
        'YYYY-MM-DD',
      )
      : null;
    await updateContextInstance(state.account.id, context.id, {
      ...context,
      description: editedDescription,
      expirationDate: parsedDate,
    });
    setIsEditingDescription(false);
    await fetchContextInstance();
  }, [editedDescription, getValues, context, fetchContextInstance, state.account.id]);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.titleContainer}>
          <div className={classes.headerIcon}>
            <img
              src={process.env.PUBLIC_URL + '/img/icons/context/business.svg'}
              alt="back"
            />
          </div>
          <div className={classes.titleWrapper}>
            <div className={classes.infoWrapper}>
              <div className={classes.title}>{context.reference}</div>
            </div>
            <div className={classes.modelName} onClick={isGlobalAdmin ? openModelPage : undefined}>
              {context.contextModelName}
            </div>
          </div>
        </div>
        {shouldDisplayInfo ? (
          <Grid container spacing={1}>
            <Grid
              item
              xs={context.isArchived ? 8 : 9}
              className={classes.description}
            >
              <div className={classes.label}>
                {i18next.t('context.contextDetail.description')}
                {canEdit&&<EAIcon
                  icon={'edit_filled'}
                  className={classes.editDescriptionIcon}
                  onClick={() => {
                    setIsEditingDescription(true);
                  }}
                />}
              </div>
              <TextField
                id="name"
                placeholder={i18next.t('notSpecified')}
                InputProps={{
                  disableUnderline: true,
                }}
                className={classes.descriptionText}
                variant="outlined"
                multiline
                disabled={!isEditingDescription || !canEdit}
                rows={4}
                value={editedDescription}
              />
            </Grid>

            <Grid item xs={3}>
              {expDateInfos()}
            </Grid>
          </Grid>
        ) : (
          <></>
        )}
        {context.isArchived && (
          <Grid item xs={4}>
            <ArchivedInfos
              expirationDate={context.expirationDate}
              updatedBy={context.updatedBy}
              motif={context.motif}
              label={i18next.t('context.ficheContext.archivedLabelContext')}
            />
          </Grid>
        )}
        <div className={classes.divider}></div>
      </div>
      <EADialog
        title={i18next.t('context.editDescription')}
        isOpen={isEditingDescription}
        onCancel={() => setIsEditingDescription(false)}
        fullWidth
        onValidate={saveDescriptionOrDate}
      >
        <TextField
          id="name"
          placeholder={''}
          variant="outlined"
          multiline
          fullWidth
          onChange={(e) => {
            setEditedDescription(e.target.value);
          }}
          rows={4}
          value={editedDescription}
        />
      </EADialog>
    </div>
  );
};

export default Informations;
